"use client";

import { useCallback, useEffect, useRef, useState } from "react";

import { addProductToList, getUserLists } from "@/app/actions/wishlist";
import { useSessionStore, useUserStore } from "@/app/store";
import type { ListKind, WishlistSummary } from "@/domain/wishlist";

export type AddToListStatus = "ready" | "error";

export interface UseAddToListUrls {
	login: string;
	createWishlist: string;
	createRegistry: string;
	listViewBase: string;
}

export interface UseAddToListParams {
	sku: string;
	quantity: number;
	urls: UseAddToListUrls;
}

export interface UseAddToList {
	isOpen: boolean;
	status: AddToListStatus;
	wishlists: WishlistSummary[];
	registries: WishlistSummary[];
	submittingPk?: string;
	addedPk?: string;
	addedListName?: string;
	errorMessage?: string;
	viewListHref?: string;
	open: () => void;
	close: () => void;
	setOpen: (next: boolean) => void;
	onSelect: (pk: string) => void;
	onCreateNew: (kind: ListKind) => void;
}

export function useAddToList(params: UseAddToListParams): UseAddToList {
	const { sku, quantity, urls } = params;

	const userType = useUserStore((state) => state.getUserType());
	const sessionState = useSessionStore((state) => state.sessionState);

	const [isOpen, setIsOpen] = useState(false);
	const [status, setStatus] = useState<AddToListStatus>("ready");
	const [wishlists, setWishlists] = useState<WishlistSummary[]>([]);
	const [registries, setRegistries] = useState<WishlistSummary[]>([]);
	const [submittingPk, setSubmittingPk] = useState<string | undefined>();
	const [addedPk, setAddedPk] = useState<string | undefined>();
	const [addedListName, setAddedListName] = useState<string | undefined>();
	const [errorMessage, setErrorMessage] = useState<string | undefined>();

	const redirect = useCallback((href: string) => {
		window.location.href = href;
	}, []);

	const goToLogin = useCallback(
		(target?: string) => {
			const dest =
				target ?? window.location.pathname + window.location.search;
			redirect(
				`${urls.login}?loginRedirectTarget=${encodeURIComponent(dest)}`
			);
		},
		[redirect, urls.login]
	);

	const loadLists = useCallback(() => {
		if (!sessionState) {
			return;
		}

		void getUserLists(sessionState).then((response) => {
			if (response.status === "failed") {
				setStatus("error");
				setErrorMessage(response.message);
				return;
			}

			setWishlists(response.wishlists);
			setRegistries(response.registries);
			setStatus("ready");
		});
	}, [sessionState]);

	const prefetchedRef = useRef(false);
	useEffect(() => {
		if (prefetchedRef.current) {
			return;
		}
		if (userType === "anonymous" || !sessionState) {
			return;
		}
		prefetchedRef.current = true;
		loadLists();
	}, [userType, sessionState, loadLists]);

	const open = useCallback(() => {
		setErrorMessage(undefined);
		setAddedPk(undefined);
		setAddedListName(undefined);
		setSubmittingPk(undefined);
		setIsOpen(true);
	}, []);

	const close = useCallback(() => {
		setIsOpen(false);
	}, []);

	const setOpen = useCallback(
		(next: boolean) => {
			if (next) {
				open();
				return;
			}
			close();
		},
		[open, close]
	);

	const onSelect = useCallback(
		(pk: string) => {
			if (!sessionState) {
				goToLogin();
				return;
			}

			setErrorMessage(undefined);
			setSubmittingPk(pk);

			void addProductToList(sessionState, {
				wishlistId: pk,
				sku,
				quantity
			}).then((response) => {
				setSubmittingPk(undefined);

				if (response.status === "success") {
					const match = [...wishlists, ...registries].find(
						(list) => list.pk === pk
					);
					setAddedPk(pk);
					setAddedListName(match?.name);
					return;
				}

				if (response.reason === "session-expired") {
					goToLogin();
					return;
				}

				setErrorMessage(response.message);
			});
		},
		[sessionState, goToLogin, sku, quantity, wishlists, registries]
	);

	const onCreateNew = useCallback(
		(kind: ListKind) => {
			const base =
				kind === "registry" ? urls.createRegistry : urls.createWishlist;
			const target = `${base}?product=${encodeURIComponent(sku)}&qty=${quantity}`;

			// Anonymous users can open the dropdown, but creating a list
			// requires auth — send them to login and resume the create flow after.
			if (userType === "anonymous") {
				goToLogin(target);
				return;
			}

			redirect(target);
		},
		[
			userType,
			urls.createRegistry,
			urls.createWishlist,
			goToLogin,
			redirect,
			sku,
			quantity
		]
	);

	const viewListHref = addedPk
		? `${urls.listViewBase}?wishlistId=${encodeURIComponent(addedPk)}`
		: undefined;

	return {
		isOpen,
		status,
		wishlists,
		registries,
		submittingPk,
		addedPk,
		addedListName,
		errorMessage,
		viewListHref,
		open,
		close,
		setOpen,
		onSelect,
		onCreateNew
	};
}
